import { gql } from '@apollo/client';
import {
	CampaignViewModel
} from '@/graphql/generated';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, AutocompleteConfig } from './generic-autocomplete';
import { CampaignCreateForm } from './campaign-create-form';

export interface CampaignAutocompleteProps {
	value?: string;
	onChange: (campaignId: string, campaign: CampaignViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	enableCreateNew?: boolean;
	initialSelectedLabel?: string;
}

// GraphQL query for campaigns autocomplete
const CAMPAIGNS_AUTOCOMPLETE_QUERY = gql`
	query CampaignsAutocomplete(
		$where: CampaignViewModelFilterInput
		$first: Int
		$after: String
	) {
		campaigns(where: $where, first: $first, after: $after) {
			edges {
				node {
					campaignId
					name
					description
					isActive
					facility {
						facilityId
						name
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

// Helper functions for campaign display
export const getCampaignDisplayName = (campaign: CampaignViewModel): string => {
	return campaign.name || 'Unnamed Campaign';
};

export const getCampaignSecondaryText = (campaign: CampaignViewModel): string => {
	// Show facility name as secondary text if available
	if (campaign.facility?.name) {
		return `Facility: ${campaign.facility.name}`;
	}
	// Show description as fallback
	if (campaign.description) {
		return campaign.description;
	}
	return '';
};

// Create campaign autocomplete configuration
const createCampaignAutocompleteConfig = (enableCreateNew = false): AutocompleteConfig<CampaignViewModel> => ({
	query: CAMPAIGNS_AUTOCOMPLETE_QUERY,
	queryName: 'campaigns',
	searchFields: ['name', 'description'],
	getDisplayName: getCampaignDisplayName,
	getSecondaryText: getCampaignSecondaryText,
	getId: (campaign) => campaign.campaignId,
	pageSize: DEFAULT_PAGE_SIZE,
	// Enable details functionality
	enableDetails: true,
	entityType: 'campaigns',
	// Base filter to only show active campaigns by default
	baseFilters: { isActive: { eq: true } },
	// Sort by name
	orderBy: [{ name: 'ASC' }],
	createNew: enableCreateNew ? {
		enabled: true,
		entityName: 'Campaign',
		createFormComponent: CampaignCreateForm,
	} : undefined,
});

export const CampaignAutocomplete: React.FC<CampaignAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search campaigns...',
	disabled = false,
	className = '',
	enableCreateNew = false,
	initialSelectedLabel
}) => {
	const config = createCampaignAutocompleteConfig(enableCreateNew);

	return (
		<GenericAutocomplete<CampaignViewModel>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			initialSelectedLabel={initialSelectedLabel}
		/>
	);
};
